import { addTrackingData, generalCallbacks, getFormName } from './general';
import { generatePassword } from '../helpers/generate-password';
import { generateUuid } from '../helpers/strings';
import { processLogin } from '../helpers/login';
import { resetCaptcha } from '../helpers/captchas';
import { detectFormTest } from '../helpers/detect-form-test';
import { TrackingHandler } from '../tracking/TrackingHandler';
import { log } from '../tracking/InternalLogger';
const REQUIRED_FIELDS = ['first_name', 'email', 'account_name', 'mobile_number_c'];
const submitData = (url, data) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.responseType = 'json';
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');
    return new Promise((resolve, reject) => {
        xhr.send(JSON.stringify(data));
        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                resolve(xhr.response);
                return;
            }
        };
    });
};
const submitCurrentFreeTrialData = (callbackData) => {
    if (!callbackData.formContainer.dataset.submitId) {
        callbackData.formContainer.dataset.submitId = generateUuid().replace(/-/g, '');
    }
    if (!callbackData.formContainer.dataset.password) {
        callbackData.formContainer.dataset.password = generatePassword(10);
    }
    const data = callbackData.data;
    data.submit_id_c = callbackData.formContainer.dataset.submitId;
    data.password = callbackData.formContainer.dataset.password;
    const freeTrialNumberBlock = callbackData.formContainer.querySelector('.free-trial-number');
    data.free_trial_type_c = freeTrialNumberBlock ? 'Show number' : 'Redirect to portal';
    const url = callbackData.formContainer.dataset.endpoint || window.endpoints.freeTrial;
    return submitData(url, addTrackingData(data));
};
const navigateFreeTrialCallback = (callbackData) => {
    generalCallbacks.navigateFormCallback(callbackData);
    for (const requiredField of REQUIRED_FIELDS) {
        if (!callbackData.data[requiredField]) {
            return true;
        }
    }
    return handleStep2Submission(callbackData);
};
const handleStep2Submission = (callbackData) => {
    const submitButton = callbackData.currentStep.querySelector('input[type="submit"], button');
    if (submitButton) {
        submitButton.setAttribute('disabled', 'disabled');
    }
    return submitUnverifiedRequest(callbackData);
};
const submitUnverifiedRequest = async (callbackData) => {
    const submitErrorContainer = callbackData.currentStep.querySelector('.submit-error-message');
    const captchaContainer = callbackData.formContainer.querySelector('.captcha-container');
    submitErrorContainer?.classList?.toggle('js-hidden', true);
    callbackData.currentStep.classList.toggle('submitting', true);
    if (!detectFormTest(callbackData.data)) {
        const formName = getFormName(callbackData.formContainer);
        TrackingHandler.getInstance().formSubmit(formName, callbackData.data);
    }
    const response = await submitCurrentFreeTrialData(callbackData);
    callbackData.currentStep.classList.toggle('submitting', false);
    if (typeof response !== 'object') {
        submitErrorContainer?.classList?.toggle('js-hidden', false);
        return false;
    }
    if (response.data?.verification_hash) {
        callbackData.formContainer.dataset.verification_hash = response.data.verification_hash;
        return true;
    }
    if (response.result === 'conflict') {
        callbackData.currentStep.classList.toggle('js-hidden', true);
        callbackData.formContainer.querySelector('.form-step:last-of-type')?.classList?.toggle('js-hidden', false);
        processFreeTrialCreateConflict(callbackData.formContainer);
        log('Form submit result', {
            formName: 'Free trial',
            result: response.result,
            isTest: detectFormTest(callbackData.data),
        });
        return false;
    }
    if (response.result !== 'success') {
        if (captchaContainer) {
            resetCaptcha(captchaContainer);
        }
        const submitButton = callbackData.currentStep.querySelector('button');
        if (submitButton) {
            submitButton.disabled = false;
        }
        if (submitErrorContainer) {
            submitErrorContainer.innerText = response.message;
            submitErrorContainer.classList.toggle('js-hidden', false);
        }
        log('Form submit result', {
            formName: 'Free trial',
            result: response.result,
            isTest: detectFormTest(callbackData.data),
        });
        return false;
    }
    callbackData.currentStep.classList.toggle('js-hidden', true);
    callbackData.formContainer.querySelector('.form-step:last-of-type')?.classList?.toggle('js-hidden', false);
    processFreeTrialSuccess(callbackData.formContainer, callbackData.data, response);
    log('Form submit result', {
        formName: 'Free trial',
        result: response.result,
        isTest: detectFormTest(callbackData.data),
    });
    return false;
};
const submitFreeTrialModifierCallback = (callbackData) => {
    const data = generalCallbacks.submitModifierCallback(callbackData);
    if (!data) {
        return;
    }
    if (callbackData.formContainer.dataset.password) {
        data.password = callbackData.formContainer.dataset.password;
    }
    if (callbackData.formContainer.dataset.verification_hash) {
        data.verification_hash = callbackData.formContainer.dataset.verification_hash;
    }
    const freeTrialNumberBlock = callbackData.formContainer.querySelector('.free-trial-number');
    data.free_trial_type_c = freeTrialNumberBlock ? 'Show number' : 'Redirect to portal';
    return data;
};
const submitFreeTrialResultCallback = (callbackData) => {
    if (callbackData.xhr.status === 409) {
        processFreeTrialCreateConflict(callbackData.formContainer);
        log('Form submit result', {
            formName: 'Free trial',
            result: 'conflict',
            isTest: detectFormTest(callbackData.data),
        });
        return true;
    }
    if (callbackData.xhr.status < 200 || callbackData.xhr.status > 299) {
        log('Form submit result', {
            formName: 'Free trial',
            result: 'error',
            isTest: detectFormTest(callbackData.data),
        });
        return true;
    }
    log('Form submit result', {
        formName: 'Free trial',
        result: 'success',
        isTest: detectFormTest(callbackData.data),
    });
    processFreeTrialSuccess(callbackData.formContainer, callbackData.data, callbackData.xhr.response);
    return true;
};
const toggleResponseContainer = (formContainer, selector, responseCustomerType = undefined, ...expectedCustomerTypes) => {
    const responseContainer = formContainer.querySelector(selector);
    if (!responseContainer) {
        return;
    }
    if (!responseCustomerType) {
        responseContainer.style.display = 'none';
        return;
    }
    responseContainer.style.display = expectedCustomerTypes.includes(responseCustomerType) ? 'block' : 'none';
};
const processFreeTrialCreateConflict = (formContainer) => {
    const createErrorMessage = formContainer.querySelector('.show-on-create-error');
    if (!createErrorMessage) {
        return;
    }
    formContainer.querySelectorAll('.free-trial-result-container').forEach((messageContainer) => (messageContainer.style.display = 'none'));
    createErrorMessage.style.display = 'block';
};
const processFreeTrialSuccess = (formContainer, submittedData, response) => {
    const freeTrialNumberBlocks = formContainer.querySelectorAll('.free-trial-number');
    if (freeTrialNumberBlocks.length > 0 && typeof response?.data?.displayNumber === 'string') {
        freeTrialNumberBlocks.forEach((freeTrialNumberBox) => (freeTrialNumberBox.innerText = response.data.displayNumber));
    }
    const freeTrialNumberLinks = formContainer.querySelectorAll('a.free-trial-number-link');
    if (freeTrialNumberLinks.length > 0 && typeof response?.data?.number === 'string') {
        freeTrialNumberLinks.forEach((freeTrialNumberLink) => (freeTrialNumberLink.href = 'tel:' + response.data.number));
    }
    const customerType = response?.data?.customerType;
    toggleResponseContainer(formContainer, '.show-on-create-error', customerType, 'NONE');
    toggleResponseContainer(formContainer, '.show-on-success', customerType, 'FREE_TRIAL');
    toggleResponseContainer(formContainer, '.show-on-exists', customerType, 'ACTIVE_FREE_TRIAL');
    toggleResponseContainer(formContainer, '.show-on-reactivated', customerType, 'REACTIVATED_FREE_TRIAL');
    toggleResponseContainer(formContainer, '.show-on-expired', customerType, 'INACTIVE');
    const loginLinks = formContainer.querySelectorAll('.login');
    loginLinks.forEach((loginLink) => {
        loginLink.addEventListener('click', () => {
            processLogin(submittedData.first_name, response.data?.password || formContainer.dataset.password, TrackingHandler.getInstance().visitorId || generateUuid());
        });
    });
    if (freeTrialNumberBlocks.length === 0 &&
        customerType &&
        ['FREE_TRIAL', 'ACTIVE_FREE_TRIAL', 'REACTIVATED_FREE_TRIAL'].includes(customerType)) {
        processLogin(submittedData.first_name, response.data?.password || formContainer.dataset.password, TrackingHandler.getInstance().visitorId || generateUuid());
    }
};
export const freeTrialCallbacks = {
    navigateFreeTrialCallback,
    submitFreeTrialModifierCallback,
    submitFreeTrialResultCallback,
};
